import { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Filter, 
  MapPin, 
  Cpu, 
  Radio,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Calendar,
  CalendarPlus,
  History,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { gardensService, controllersService, sensorsService, companiesService } from '@/services';
import type { ApiGarden, CreateGardenRequest, UpdateGardenRequest, CreateZoneRequest, UpdateZoneRequest, ZoneAssignmentRequest } from '@/types/api';
import type { Garden, ControllerModel } from '@/types';
import { GardenFormDialog } from '@/components/gardens/GardenFormDialog';
import { GardenDetailDialog } from '@/components/gardens/GardenDetailDialog';
import { GardenSchedulesDialog } from '@/components/gardens/GardenSchedulesDialog';
import { GardenDeleteDialog } from '@/components/gardens/GardenDeleteDialog';
import { MassScheduleAssignDialog } from '@/components/gardens/MassScheduleAssignDialog';
import { GardenHistoryDialog } from '@/components/gardens/GardenHistoryDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

// Lazy load the map component
const GardenMap = lazy(() =>
  import('@/components/dashboard/GardenMap').then((module) => ({
    default: module.GardenMap,
  }))
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function MapFallback() {
  return (
    <Card>
      <CardContent className="p-0 overflow-hidden rounded-xl">
        <div className="h-[400px] w-full flex items-center justify-center bg-muted/50">
          <div className="text-center">
            <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2 animate-pulse" />
            <p className="text-sm text-muted-foreground">Cargando mapa...</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GardenCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-6 w-20" />
      </CardContent>
    </Card>
  );
}

/**
 * Helper function to convert API Garden to Frontend Garden
 * Defined outside component to avoid initialization issues
 */
function convertApiGarden(apiGarden: ApiGarden): Garden | null {
  if (!apiGarden || !apiGarden.id) return null;
  
  const coords = apiGarden.coordinates;
  const hasValidCoords = coords && typeof coords.lat === 'number' && typeof coords.lng === 'number';
  
  return {
    id: apiGarden.id,
    name: apiGarden.name || '',
    description: apiGarden.description || '',
    companyId: apiGarden.companyId || '',
    municipality: apiGarden.municipality || '',
    coordinates: hasValidCoords 
      ? { lat: coords.lat, lng: coords.lng }
      : { lat: 39.47, lng: -0.38 }, // Default: Valencia
    area: apiGarden.area,
    zones: [],
    controllers: [],
    sensors: [],
    schedules: [],
    triggers: [],
    createdAt: apiGarden.createdAt ? new Date(apiGarden.createdAt) : new Date(),
    updatedAt: apiGarden.updatedAt ? new Date(apiGarden.updatedAt) : new Date(),
    zoneCount: apiGarden.zoneCount ?? 0,
    controllerCount: apiGarden.controllerCount ?? 0,
    sensorCount: apiGarden.sensorCount ?? 0,
  };
}

export default function Gardens() {
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentUser, isSuperAdmin } = useAuth();
  
  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [schedulesDialogOpen, setSchedulesDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [massAssignDialogOpen, setMassAssignDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedGarden, setSelectedGarden] = useState<Garden | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // Query para obtener jardines
  // AJUSTE: Usar parámetros search y companyId del backend según documentación validada
  const { 
    data: gardensData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['gardens', searchTerm, currentUser?.companyId],
    queryFn: async () => {
      try {
        // Preparar parámetros según documentación: page, pageSize, search, companyId
        const params: { pageSize: number; search?: string; companyId?: string } = { 
          pageSize: 100 
        };
        
        // Si hay término de búsqueda, usar parámetro search del backend
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }
        
        // Si el usuario es company_admin, filtrar por su empresa
        if (currentUser?.role === 'company_admin' && currentUser.companyId) {
          params.companyId = currentUser.companyId;
        }
        
        const response = await gardensService.getAll(params);
        if (!response.success) {
          console.error('[Gardens] Error response:', response);
          throw new Error(response.error?.message || `Error al cargar jardines: ${response.error?.code || 'Unknown error'}`);
        }
        return response.data;
      } catch (err) {
        console.error('[Gardens] Error fetching gardens:', err);
        throw err;
      }
    },
    retry: 1,
  });

  // Query para obtener controladoras - con polling cada 5 minutos (sin WebSocket)
  const { data: controllersData } = useQuery({
    queryKey: ['controllers'],
    queryFn: async () => {
      const response = await controllersService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar controladoras');
      }
      return response.data;
    },
    refetchInterval: 300000, // 5 minutos
    refetchIntervalInBackground: false,
  });

  // Query para obtener sensores - con polling cada 5 minutos (sin WebSocket)
  const { data: sensorsData } = useQuery({
    queryKey: ['sensors'],
    queryFn: async () => {
      const response = await sensorsService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar sensores');
      }
      return response.data;
    },
    refetchInterval: 300000, // 5 minutos
    refetchIntervalInBackground: false,
  });

  // Query para obtener empresas (para el formulario)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => gardensService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        // Invalidar queries de forma segura
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['gardens'] });
        });
        toast({ title: 'Jardín eliminado', description: 'El jardín ha sido eliminado.', variant: 'destructive' });
        setDeleteDialogOpen(false);
        setSelectedGarden(null);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Procesar datos de forma segura
  const gardensArray = gardensData?.items;
  const controllersArray = controllersData?.items;
  const sensorsArray = sensorsData?.items;
  const companiesArray = companiesData?.items;

  // Convertir gardens
  const gardens: Garden[] = Array.isArray(gardensArray)
    ? gardensArray.map(convertApiGarden).filter((g): g is Garden => g !== null)
    : [];

  // AJUSTE: Ya no se filtra en el cliente, el backend hace la búsqueda con el parámetro search
  // Si el usuario es company_admin, el backend ya filtra por companyId
  const filteredGardens: Garden[] = gardens;

  // Convert controllers for map component
  const controllersForMap = Array.isArray(controllersArray) && controllersArray.length > 0
    ? controllersArray
        .filter(c => c && c.id)
        .map(c => {
          const coords = c.coordinates;
          const hasValidCoords = coords && typeof coords.lat === 'number' && typeof coords.lng === 'number';
          
          return {
            id: c.id,
            name: c.name || '',
            model: (c.model || 'UC511_V2') as ControllerModel,
            deviceEUI: c.deviceEUI || '',
            companyId: c.companyId || null,
            coordinates: hasValidCoords ? { lat: coords.lat, lng: coords.lng } : undefined,
            communicationType: c.communicationType || 'mqtt',
            valves: Array.isArray(c.valves) ? c.valves.map(v => ({
              id: v.id || '',
              controllerId: c.id,
              channel: v.channel || 1,
              name: v.name || '',
              status: v.status || 'closed',
            })) : [],
            status: c.status || 'offline',
            lastSeen: c.lastSeen ? new Date(c.lastSeen) : undefined,
            battery: c.battery,
            createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
            updatedAt: c.updatedAt ? new Date(c.updatedAt) : new Date(),
          };
        })
    : [];

  // Convert sensors for map component
  const sensorsForMap = Array.isArray(sensorsArray) && sensorsArray.length > 0
    ? sensorsArray
        .filter(s => s && s.id)
        .map(s => {
          const coords = s.coordinates;
          const hasValidCoords = coords && typeof coords.lat === 'number' && typeof coords.lng === 'number';
          
          return {
            id: s.id,
            name: s.name || '',
            model: (s.model || 'EM500-SMTC') as 'EM500-SMTC',
            deviceEUI: s.deviceEUI || '',
            companyId: s.companyId || null,
            zoneId: undefined,
            coordinates: hasValidCoords ? { lat: coords.lat, lng: coords.lng } : undefined,
            depth: undefined,
            communicationType: s.communicationType || 'mqtt',
            status: s.status || 'offline',
            lastSeen: s.lastSeen ? new Date(s.lastSeen) : undefined,
            battery: s.battery,
            lastReading: undefined,
            createdAt: s.createdAt ? new Date(s.createdAt) : new Date(),
            updatedAt: s.updatedAt ? new Date(s.updatedAt) : new Date(),
          };
        })
    : [];

  // Handlers
  const handleNewGarden = () => {
    setSelectedGarden(null);
    setFormDialogOpen(true);
  };

  const handleEditGarden = (garden: Garden) => {
    setSelectedGarden(garden);
    setFormDialogOpen(true);
  };

  const handleViewDetails = (garden: Garden) => {
    setSelectedGarden(garden);
    setDetailDialogOpen(true);
  };

  const handleViewSchedules = (garden: Garden) => {
    setSelectedGarden(garden);
    setSchedulesDialogOpen(true);
  };

  const handleViewHistory = (garden: Garden) => {
    setSelectedGarden(garden);
    setHistoryDialogOpen(true);
  };

  const handleDeleteClick = (garden: Garden, e?: React.MouseEvent) => {
    // Prevenir que el evento se propague al mapa
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedGarden(garden);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: Partial<Garden>) => {
    setIsFormSubmitting(true);
    
    try {
      // Preparar datos según la documentación de la API
      // POST /v1/gardens espera: name, description, companyId, municipality, lat, lng, area
      const gardenData: CreateGardenRequest = {
        name: data.name!,
        description: data.description,
        companyId: data.companyId!,
        municipality: data.municipality!,
        lat: data.coordinates?.lat,
        lng: data.coordinates?.lng,
        area: data.area,
      };

      let gardenId: string;

      if (selectedGarden) {
        // Actualizar jardín existente
        // AJUSTE: companyId NO se puede actualizar según documentación validada
        const updateData: UpdateGardenRequest = {
          name: gardenData.name,
          description: gardenData.description,
          municipality: gardenData.municipality,
          lat: gardenData.lat,
          lng: gardenData.lng,
          area: gardenData.area,
          // NO incluir companyId - no se puede actualizar
        };
        
        const response = await gardensService.update(selectedGarden.id, updateData);
        if (!response.success) {
          toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
          return;
        }
        gardenId = selectedGarden.id;
      } else {
        // Crear nuevo jardín
        const response = await gardensService.create(gardenData);
        if (!response.success || !response.data) {
          toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
          return;
        }
        gardenId = response.data.id;
      }

      // Procesar zonas si existen
      if (data.zones && data.zones.length > 0) {
        // Obtener zonas existentes del jardín
        const existingZonesResponse = await gardensService.getZones(gardenId);
        const existingZones = existingZonesResponse.success ? (existingZonesResponse.data?.items || []) : [];
        
        // Procesar cada zona del formulario
        for (const zone of data.zones) {
          const isNewZone = zone.id.startsWith('zone-temp-');
          let zoneId: string;

          if (isNewZone) {
            // Crear nueva zona
            const createZoneData: CreateZoneRequest = {
              name: zone.name,
              sensorId: zone.sensorId || undefined,
            };
            const createZoneResponse = await gardensService.createZone(gardenId, createZoneData);
            if (!createZoneResponse.success || !createZoneResponse.data) {
              console.error('Error al crear zona:', createZoneResponse.error);
              continue;
            }
            zoneId = createZoneResponse.data.id;
          } else {
            // Actualizar zona existente
            zoneId = zone.id;
            const existingZone = existingZones.find(z => z.id === zoneId);
            
            if (existingZone) {
              const updateZoneData: UpdateZoneRequest = {
                name: zone.name,
                sensorId: zone.sensorId || null,
              };
              await gardensService.updateZone(gardenId, zoneId, updateZoneData);
            }
          }

          // Procesar asignaciones de controladores/válvulas
          const allControllerAssignments = zone.controllerAssignments?.filter(ca => ca.controllerId) || [];
          const existingControllerAssignments = allControllerAssignments.filter(ca => !ca.id.startsWith('ca-temp-'));
          const newControllerAssignments = allControllerAssignments.filter(ca => ca.id.startsWith('ca-temp-'));

          // Obtener asignaciones existentes de la zona
          const zoneResponse = await gardensService.getZoneById(gardenId, zoneId);
          const existingAssignments = zoneResponse.success && zoneResponse.data?.controllerAssignments 
            ? zoneResponse.data.controllerAssignments 
            : [];

          // Eliminar asignaciones que ya no existen en el formulario
          for (const existingAssignment of existingAssignments) {
            const stillExists = existingControllerAssignments.some(ca => 
              ca.controllerId === existingAssignment.controllerId && 
              ca.valveChannel === existingAssignment.valveChannel
            );
            if (!stillExists) {
              await gardensService.unassignFromZone(gardenId, zoneId, existingAssignment.id);
            }
          }

          // Crear nuevas asignaciones
          for (const assignment of newControllerAssignments) {
            if (assignment.controllerId) {
              const assignData: ZoneAssignmentRequest = {
                controllerId: assignment.controllerId,
                valveChannel: assignment.valveChannel,
              };
              await gardensService.assignToZone(gardenId, zoneId, assignData);
            }
          }
        }

        // Eliminar zonas que ya no existen en el formulario
        for (const existingZone of existingZones) {
          const stillExists = data.zones.some(z => z.id === existingZone.id);
          if (!stillExists) {
            await gardensService.deleteZone(gardenId, existingZone.id);
          }
        }
      } else {
        // Si no hay zonas en el formulario, eliminar todas las existentes
        const existingZonesResponse = await gardensService.getZones(gardenId);
        if (existingZonesResponse.success) {
          const existingZones = existingZonesResponse.data?.items || [];
          for (const zone of existingZones) {
            await gardensService.deleteZone(gardenId, zone.id);
          }
        }
      }

      // Invalidar queries de forma segura
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['gardens'] });
        queryClient.invalidateQueries({ queryKey: ['garden-zones', gardenId] });
      });
      
      toast({ 
        title: selectedGarden ? 'Jardín actualizado' : 'Jardín creado', 
        description: 'El jardín y sus zonas han sido guardados correctamente.' 
      });
      setFormDialogOpen(false);
      setSelectedGarden(null);
    } catch (error) {
      console.error('Error al guardar jardín:', error);
      toast({ 
        title: 'Error', 
        description: error instanceof Error ? error.message : 'Error al guardar', 
        variant: 'destructive' 
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDeleteConfirm = (gardenId: string) => {
    deleteMutation.mutate(gardenId);
  };

  const handleToggleSchedule = (scheduleId: string, enabled: boolean) => {
    toast({ 
      title: enabled ? 'Programación activada' : 'Programación desactivada',
    });
  };

  const handleUpdateZoneSchedules = (gardenId: string, zones: any[]) => {
    toast({ title: 'Zonas actualizadas' });
  };

  const handleMassAssign = (updatedGardens: Garden[]) => {
    toast({ title: 'Programaciones asignadas a todos los jardines' });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Jardines</h1>
            <p className="text-muted-foreground mt-1">Gestiona tus jardines y zonas de riego</p>
          </div>
        </div>
        <Skeleton className="h-[400px]" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <GardenCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Jardines</h1>
            <p className="text-muted-foreground mt-1">Gestiona tus jardines y zonas de riego</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar jardines'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Jardines
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona tus jardines y zonas de riego
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setMassAssignDialogOpen(true)}
            className="gap-2"
          >
            <CalendarPlus className="w-4 h-4" />
            Asignar Masivo
          </Button>
          <Button 
            onClick={handleNewGarden}
            className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Jardín
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar jardines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="gap-2">
          <Filter className="w-4 h-4" />
          Filtros
        </Button>
      </div>

      {/* Map - centrado en el primer jardín */}
      <Suspense fallback={<MapFallback />}>
        <GardenMap
          gardens={filteredGardens}
          controllers={controllersForMap}
          sensors={sensorsForMap}
          center={filteredGardens.length > 0 && filteredGardens[0]?.coordinates
            ? [filteredGardens[0].coordinates.lat, filteredGardens[0].coordinates.lng] 
            : [39.47, -0.38]} // Default: Valencia
          zoom={filteredGardens.length === 1 ? 15 : 10}
          disabled={deleteDialogOpen || formDialogOpen || detailDialogOpen || massAssignDialogOpen || historyDialogOpen}
        />
      </Suspense>

      {/* Gardens grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredGardens.map((garden) => (
          <motion.div key={garden.id} variants={itemVariants}>
            <Card className="group hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="font-display font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                      {garden.name}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{garden.municipality}</span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetails(garden)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Ver detalles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEditGarden(garden)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewSchedules(garden)}>
                        <Calendar className="w-4 h-4 mr-2" />
                        Programaciones
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewHistory(garden)}>
                        <History className="w-4 h-4 mr-2" />
                        Histórico
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(garden, e);
                        }}
                        className="text-destructive focus:text-destructive"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {garden.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {garden.description}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-sm">
                    <Cpu className="w-4 h-4 text-success" />
                    <span className="text-muted-foreground">
                      {garden.controllerCount ?? 0} controlador(es)
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Radio className="w-4 h-4 text-info" />
                    <span className="text-muted-foreground">
                      {garden.sensorCount ?? 0} sensor(es)
                    </span>
                  </div>
                </div>

                {/* Area */}
                {garden.area && (
                  <Badge variant="secondary" className="font-normal">
                    {garden.area.toLocaleString()} m²
                  </Badge>
                )}

                {/* Coordinates */}
                <div className="pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {garden.coordinates?.lat?.toFixed(4)}, {garden.coordinates?.lng?.toFixed(4)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredGardens.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No se encontraron jardines.
          </p>
          <Button className="mt-4" variant="outline" onClick={handleNewGarden}>
            <Plus className="w-4 h-4 mr-2" />
            Crear primer jardín
          </Button>
        </motion.div>
      )}

      {/* Dialogs */}
      <GardenFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        garden={selectedGarden || undefined}
        companies={Array.isArray(companiesArray) ? companiesArray.map(c => ({ id: c.id, name: c.name })) : []}
        controllers={Array.isArray(controllersArray) ? controllersArray : []}
        sensors={Array.isArray(sensorsArray) ? sensorsArray : []}
        onSubmit={handleFormSubmit}
        isLoading={isFormSubmitting}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
      />

      <GardenDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        garden={selectedGarden}
      />

      <GardenSchedulesDialog
        open={schedulesDialogOpen}
        onOpenChange={setSchedulesDialogOpen}
        garden={selectedGarden}
        onToggleSchedule={handleToggleSchedule}
        onUpdateZoneSchedules={handleUpdateZoneSchedules}
      />

      <GardenDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        garden={selectedGarden}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />

      <MassScheduleAssignDialog
        open={massAssignDialogOpen}
        onOpenChange={setMassAssignDialogOpen}
        gardens={gardens}
        onAssign={handleMassAssign}
      />

      <GardenHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        garden={selectedGarden}
      />
    </motion.div>
  );
}
